/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/utils/eventPhase.ts
 *
 * Rôle :
 *   Utilitaires purs (sans React, sans I/O) pour calculer la **phase**
 *   de l'événement TNE 2026 :
 *     - 'before'  : la date courante est antérieure au début.
 *     - 'during'  : la date courante est dans l'intervalle [start, end]
 *                   (bornes incluses, voir §F12).
 *     - 'after'   : la date courante est postérieure à la fin.
 *
 *   Et des helpers (`daysUntil`, `hoursUntil`, `minutesUntil`,
 *   `daysSince`) pour exposer un compte à rebours / compte à rebrousse
 *   pertinent à l'utilisateur (ex. « J-12 », « il y a 3 jours »).
 *
 * Réutilisable :
 *   100 %, fonctions pures, aucune dépendance externe (pas de Day.js
 *   ici, on reste sur l'API Date native pour faciliter le portage NOLAS
 *   / autre salon DRANE). Idempotent : appel avec les mêmes arguments
 *   → mêmes valeurs de retour. Pas d'effet de bord.
 *
 * Voir : CLAUDE.md §F12 (mode avant/pendant/après), §14.0 (métadonnées
 *        événement). Hook React associé : `src/hooks/useEventPhase.ts`.
 *
 * @author Mika
 * @since 2026-05-06
 */

/**
 * Phase de l'événement TNE 2026 du point de vue de l'utilisateur.
 *
 * - `'before'` : avant l'ouverture (J-N, J-1, etc.), l'app affiche
 *   les contenus pré-événement (présentation, inscriptions, programme,
 *   compte à rebours).
 * - `'during'` : pendant l'événement (9h-17h le 27 mai 2026 par défaut)
 *   - l'app active le scanner, la carte temps réel, les Q&A live.
 * - `'after'` : après l'événement, l'app bascule en mode rétrospective
 *   (replays, photos, transcriptions, formulaire de feedback).
 */
export type EventPhase = 'before' | 'during' | 'after';

/**
 * Calcule la phase de l'événement à un instant donné.
 *
 * **Règle des bornes** (cohérent §F12) : `start` et `end` sont **inclus**
 * dans la phase `'during'`. Donc `eventPhase(start, start, end) === 'during'`
 * et `eventPhase(end, start, end) === 'during'`. La phase `'after'` ne
 * commence qu'à la première milliseconde strictement après `end`.
 *
 * @param now    Instant de référence (typiquement `new Date()`).
 * @param start  Début de l'événement (ISO ou `Date`).
 * @param end    Fin de l'événement.
 * @returns      `'before' | 'during' | 'after'`.
 *
 * @example
 *   const start = new Date('2026-05-27T09:00:00+02:00');
 *   const end   = new Date('2026-05-27T17:00:00+02:00');
 *   eventPhase(new Date('2026-05-26T23:59:59+02:00'), start, end); // 'before'
 *   eventPhase(new Date('2026-05-27T09:00:00+02:00'), start, end); // 'during'
 *   eventPhase(new Date('2026-05-27T18:00:00+02:00'), start, end); // 'after'
 */
export function eventPhase(now: Date, start: Date, end: Date): EventPhase {
  const t = now.getTime();
  const s = start.getTime();
  const e = end.getTime();
  if (t < s) return 'before';
  if (t > e) return 'after';
  return 'during';
}

// =============================================================================
// Helpers de durée (différences arrondies vers 0)
// =============================================================================

/** Une milliseconde, utilisée comme base de tous les helpers. */
const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Nombre de **jours pleins** entre `now` et `target`, arrondi vers
 * zéro (truncation, pas floor) pour qu'une différence de 23 h 59 min
 * reste « 0 jour » dans les deux sens. Cohérent avec l'UX d'un compte
 * à rebours type « J-12 ».
 *
 * @param now     Instant de référence.
 * @param target  Cible (futur ou passé).
 * @returns       Différence en jours, signée (positif si `target` futur,
 *                négatif si passé). Toujours un entier (tronqué).
 *
 * @example
 *   daysUntil(new Date('2026-05-15'), new Date('2026-05-27')); // 12
 *   daysUntil(new Date('2026-05-27T08:00'), new Date('2026-05-27T20:00')); // 0
 *   daysUntil(new Date('2026-05-28'), new Date('2026-05-27')); // -1
 */
export function daysUntil(now: Date, target: Date): number {
  const diffMs = target.getTime() - now.getTime();
  return Math.trunc(diffMs / MS_PER_DAY);
}

/**
 * Nombre de **jours CIVILS** (calendaires) entre `now` et `target`,
 * dans le fuseau local du navigateur.
 *
 * Contrairement à `daysUntil` qui compte les heures cumulées (et donc
 * renvoie 0 quand il reste 14h avant l'événement), cette fonction
 * compare uniquement les **dates calendaires** (année / mois / jour).
 *
 * Convention UX « J-N » française : nombre de jours calendaires
 * AVANT le jour J.
 *
 *   - 26 mai 19h → 27 mai 09h : `daysUntil`=0, `daysUntilCalendar`=1 → « J-1 »
 *   - 27 mai 06h → 27 mai 09h : `daysUntil`=0, `daysUntilCalendar`=0 → « J-0 »
 *   - 25 mai 23h → 27 mai 09h : `daysUntil`=1, `daysUntilCalendar`=2 → « J-2 »
 *
 * @example
 *   daysUntilCalendar(new Date('2026-05-26T19:00'),
 *                     new Date('2026-05-27T09:00')); // 1
 *
 * FIX 2026-05-26 : Bug « J-0 alors que l'événement est demain » :
 * le calcul utilisait `Math.trunc(heures/24)`, donc renvoyait 0 dès
 * qu'il restait moins de 24h. Cf. CLAUDE.md §5 entrée 2026-05-26.
 */
export function daysUntilCalendar(now: Date, target: Date): number {
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  // Math.round pour tolérer les passages d'heure été/hiver (±1h sur la diff).
  return Math.round((targetMidnight - nowMidnight) / MS_PER_DAY);
}

/**
 * Nombre d'**heures pleines** entre `now` et `target`. Mêmes règles
 * que `daysUntil` (arrondi vers 0, valeur signée).
 */
export function hoursUntil(now: Date, target: Date): number {
  const diffMs = target.getTime() - now.getTime();
  return Math.trunc(diffMs / MS_PER_HOUR);
}

/**
 * Nombre de **minutes pleines** entre `now` et `target`. Mêmes règles
 * que `daysUntil` (arrondi vers 0, valeur signée).
 */
export function minutesUntil(now: Date, target: Date): number {
  const diffMs = target.getTime() - now.getTime();
  return Math.trunc(diffMs / MS_PER_MINUTE);
}

/**
 * Nombre de **jours pleins écoulés** depuis `target` jusqu'à `now`.
 * Renvoie 0 si `target` est dans le futur (pas de valeur négative).
 *
 * @param now     Instant de référence.
 * @param target  Cible passée (typiquement la date de fin de l'événement).
 * @returns       Différence en jours, ≥ 0. Toujours un entier (tronqué).
 *
 * @example
 *   daysSince(new Date('2026-05-30'), new Date('2026-05-27')); // 3
 *   daysSince(new Date('2026-05-26'), new Date('2026-05-27')); // 0 (target futur)
 */
export function daysSince(now: Date, target: Date): number {
  const diffMs = now.getTime() - target.getTime();
  if (diffMs <= 0) return 0;
  return Math.trunc(diffMs / MS_PER_DAY);
}

// ─────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//   Ces 5 fonctions sont **pures** et n'utilisent que `Date` natif. On
//   peut les copier-coller sans rien modifier dans NOLAS pour ajouter
//   un comportement « avant/pendant/après » au salon de l'emploi.
//   L'implémentation côté hook React (`useEventPhase`) est, elle,
//   spécifique à React 18 + TanStack Query, non portable telle quelle
//   en JS pur, mais facile à transposer (poll setInterval + fetch).
// ─────────────────────────────────────────────────────────────────────────

/**
 * Jour civil « AAAA-MM-JJ » d'un instant, DANS LE FUSEAU DE L'ÉVÉNEMENT.
 * Le navigateur peut être réglé sur un autre fuseau : « le jour J » se juge
 * à l'heure du salon, pas à celle du téléphone. Fuseau illisible : celui du
 * navigateur. Mikael - chef de projet - 2026-10-07
 */
export function jourCivil(d: Date, fuseau: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: fuseau, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }
}

/**
 * Jours civils entre `now` et `target` dans le fuseau de l'événement
 * (« J-2 » = 2). Négatif si `target` est passé.
 * Mikael - chef de projet - 2026-10-07
 */
export function joursCivilsAvant(now: Date, target: Date, fuseau: string): number {
  const a = Date.parse(jourCivil(now, fuseau) + 'T00:00:00Z');
  const b = Date.parse(jourCivil(target, fuseau) + 'T00:00:00Z');
  if (Number.isNaN(a) || Number.isNaN(b)) return daysUntilCalendar(now, target);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Sommes-nous LE JOUR de l'événement (date de début), à l'heure du salon ?
 * Vrai dès minuit, même avant l'ouverture (la phase vaut encore 'before'
 * le matin) : le jour J, aucun compte à rebours, ni « J-0 ».
 * Mikael - chef de projet - 2026-10-07
 */
export function estJourJ(now: Date, start: Date, fuseau: string): boolean {
  return jourCivil(now, fuseau) === jourCivil(start, fuseau);
}
